<template>
  <el-dialog
    :title="notice ? notice.title : 'Notice'"
    :visible.sync="dialogVisible"
    width="960px"
    top="5vh"
    custom-class="notice-report-dialog"
    append-to-body
  >
    <div class="notice-report">
      <!-- Target di kiri; Select Company di kanan, selebar dan sejajar dengan kotak Search -->
      <div class="notice-report__header">
        <!-- ada dropdown: Target bertumpuk seperti Select Company supaya label dan isinya sejajar -->
        <div v-if="roots.length" class="notice-report__field">
          <span class="notice-report__field-label">Target</span>
          <span class="notice-report__target-value">{{ targetLabel }}<span v-if="notice && notice.is_global" class="notice-global-badge">Global</span></span>
        </div>
        <div v-else class="notice-report__target">
          Target: <strong>{{ targetLabel }}</strong><span v-if="notice && notice.is_global" class="notice-global-badge">Global</span>
        </div>

        <!-- notice global: laporan per root, seperti Select Company di Agency List (Keputusan 16) -->
        <div v-if="roots.length" class="notice-report__field">
          <span class="notice-report__field-label"><i class="fas fa-building pr-2"></i>Select Company</span>
          <el-select
            v-model="rootId"
            filterable
            default-first-option
            placeholder="Select Company"
            class="notice-report__root-select"
            :disabled="loading"
            @change="changeRoot"
          >
            <el-option v-for="root in roots" :key="root.id" :value="root.id" :label="root.name"></el-option>
          </el-select>
        </div>
      </div>

      <div class="notice-report__toolbar">
        <div class="notice-report__tabs">
          <button
            type="button"
            class="notice-report__tab"
            :class="{ 'is-active': tab === 'dismissed' }"
            @click="changeTab('dismissed')"
          >Dismissed ({{ counts.dismissed }})</button>
          <button
            type="button"
            class="notice-report__tab"
            :class="{ 'is-active': tab === 'not_dismissed' }"
            @click="changeTab('not_dismissed')"
          >Not dismissed ({{ counts.not_dismissed }})</button>
        </div>
        <el-input
          v-model="search"
          clearable
          prefix-icon="el-icon-search"
          placeholder="Search name, email or agency"
          class="notice-report__search"
          @input="onSearchInput"
          @clear="reload"
        ></el-input>
      </div>

      <div class="notice-report__groups">
        <div v-if="loading" class="notice-report__empty">
          <i class="fas fa-spinner fa-pulse"></i> Loading data...
        </div>
        <template v-else>
          <div v-for="group in groups" :key="group.company_id" class="notice-report__group">
            <button type="button" class="notice-report__group-header" @click="toggleGroup(group.company_id)">
              <i :class="expanded[group.company_id] ? 'fas fa-caret-down' : 'fas fa-caret-right'"></i>
              <span class="notice-report__group-name">{{ group.label }}: {{ group.name }}</span>
              <span class="notice-report__group-count">{{ group.dismissed }} / {{ group.total }}</span>
            </button>
            <div v-if="expanded[group.company_id]" class="notice-report__users">
              <!-- setiap bagian (agency / client) punya pagination sendiri; sub-judul hanya untuk target Agency + Client -->
              <!-- Agency + Client: tiap bagian jadi kotak sendiri supaya batas Agency dan Clients jelas -->
              <div
                v-for="section in group.sections"
                :key="section.key"
                class="notice-report__section"
                :class="{ 'notice-report__section--boxed': showSectionHeadings }"
              >
                <div v-if="showSectionHeadings" class="notice-report__section-header">
                  <span><i class="fa-fw" :class="sectionIcon(section.key)"></i> {{ sectionLabel(section.key) }}</span>
                  <span>{{ section.dismissed }} / {{ section.total }}</span>
                </div>
                <div v-if="sectionLoading[sectionKey(group, section)]" class="notice-report__empty notice-report__empty--small">
                  <i class="fas fa-spinner fa-pulse"></i> Loading data...
                </div>
                <table v-else>
                  <tr v-for="user in section.users" :key="user.id">
                    <td class="notice-report__user-name">{{ user.name || '-' }}</td>
                    <td>{{ user.email }}</td>
                    <td class="notice-report__user-role">{{ user.role }}</td>
                    <td v-if="tab === 'dismissed'" class="notice-report__user-date">
                      <i class="fas fa-check"></i> {{ user.dismissed_at }}
                    </td>
                  </tr>
                </table>
                <!-- selalu tampil, sama seperti daftar client di Agency List, supaya jumlah isi selalu jelas -->
                <div class="notice-report__section-footer">
                  <span class="notice-report__showing">
                    Showing {{ section.pagination.from }} to {{ section.pagination.to }} of {{ section.pagination.total }} {{ sectionUnit(section.key) }}
                  </span>
                  <base-pagination
                    class="pagination-no-border"
                    :value="section.pagination.current_page"
                    :per-page="section.pagination.per_page"
                    :total="section.pagination.total"
                    @input="loadSection(group, section, $event)"
                  ></base-pagination>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!groups.length" class="notice-report__empty">{{ errorMessage || 'No users found' }}</div>
        </template>
      </div>

      <div v-if="pagination.total > 0" class="notice-report__footer">
        <span class="notice-report__showing">Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} groups</span>
        <base-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="pagination.total"
          @input="loadReport"
        ></base-pagination>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { Dialog, Select, Option } from 'element-ui';
import { BasePagination } from 'src/components';

const SEARCH_DEBOUNCE_MS = 500;

/**
 * TASK-EMM-1020: laporan siapa sudah dan belum dismiss, dikelompokkan per agency (Keputusan 8).
 * Label "Dismissed", bukan "Read": dismiss tidak sama dengan membaca.
 */
export default {
  name: 'NoticeReportModal',
  components: {
    BasePagination,
    [Dialog.name]: Dialog,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    notice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      tab: 'dismissed',
      search: '',
      searchTimer: null,
      loading: false,
      requestId: 0,
      errorMessage: '',
      counts: { dismissed: 0, not_dismissed: 0 },
      // hanya terisi untuk notice global; null = root pemilik (default dari backend)
      rootId: null,
      roots: [],
      groups: [],
      expanded: {},
      // bagian yang sedang memuat halaman lain, kunci "companyId:section"
      sectionLoading: {},
      pagination: {
        perPage: 10,
        currentPage: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    targetLabel() {
      if (!this.notice) return '';
      const targets = [];
      if (this.notice.target_agency) targets.push('Agency');
      if (this.notice.target_client) targets.push('Client');
      return targets.join(', ');
    },
    // target tunggal sudah jelas dari "Target:" dan label grup, jadi sub-judul hanya untuk Agency + Client
    showSectionHeadings() {
      return !!(this.notice && this.notice.target_agency && this.notice.target_client);
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.tab = 'dismissed';
        this.search = '';
        this.counts = { dismissed: 0, not_dismissed: 0 };
        this.rootId = null;
        this.roots = [];
        this.reload();
      } else {
        clearTimeout(this.searchTimer);
      }
    },
  },
  methods: {
    changeTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.reload();
    },
    // ganti root: pencarian dikosongkan dan kembali ke halaman 1, sama seperti ganti tab
    changeRoot() {
      this.search = '';
      this.reload();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, SEARCH_DEBOUNCE_MS);
    },
    reload() {
      clearTimeout(this.searchTimer);
      this.pagination.currentPage = 1;
      this.loadReport();
    },
    toggleGroup(companyId) {
      this.expanded = Object.assign({}, this.expanded, { [companyId]: !this.expanded[companyId] });
    },
    sectionKey(group, section) {
      return `${group.company_id}:${section.key}`;
    },
    sectionLabel(key) {
      return key === 'client' ? 'Clients' : 'Agency';
    },
    sectionIcon(key) {
      return key === 'client' ? 'fas fa-users' : 'fas fa-user-tie';
    },
    sectionUnit(key) {
      return key === 'client' ? 'clients' : 'agency users';
    },
    async loadSection(group, section, page) {
      if (!this.notice || page === section.pagination.current_page) return;
      const key = this.sectionKey(group, section);
      const requestId = this.requestId;
      this.sectionLoading = Object.assign({}, this.sectionLoading, { [key]: true });
      try {
        const data = await this.$store.dispatch('fetchNoticeReport', {
          id: this.notice.id,
          tab: this.tab,
          search: this.search,
          groupId: group.company_id,
          section: section.key,
          sectionPage: page,
          rootId: this.rootId,
        });
        // laporan sudah dimuat ulang (ganti tab, cari, atau pindah halaman grup): hasil lama dibuang
        if (requestId !== this.requestId) return;
        const index = group.sections.indexOf(section);
        if (index !== -1) {
          this.$set(group.sections, index, data.section);
        }
      } catch (message) {
        if (requestId !== this.requestId) return;
        this.$notify({ type: 'danger', message: message || 'Failed to fetch report', icon: 'fas fa-bug' });
      } finally {
        this.sectionLoading = Object.assign({}, this.sectionLoading, { [key]: false });
      }
    },
    async loadReport() {
      if (!this.notice) return;
      const requestId = ++this.requestId;
      this.loading = true;
      this.errorMessage = '';
      this.sectionLoading = {};
      try {
        const data = await this.$store.dispatch('fetchNoticeReport', {
          id: this.notice.id,
          tab: this.tab,
          search: this.search,
          page: this.pagination.currentPage,
          rootId: this.rootId,
        });
        if (requestId !== this.requestId) return;
        this.roots = data.roots || [];
        this.rootId = this.roots.length ? data.root_id : null;
        this.counts = data.counts;
        this.groups = data.groups;
        this.pagination.currentPage = data.pagination.current_page;
        this.pagination.total = data.pagination.total;
        this.pagination.from = data.pagination.from;
        this.pagination.to = data.pagination.to;
        // saat mencari semua grup dibuka; tanpa pencarian hanya grup pertama
        const expanded = {};
        this.groups.forEach((group, index) => {
          expanded[group.company_id] = this.search.trim() !== '' || index === 0;
        });
        this.expanded = expanded;
      } catch (message) {
        if (requestId !== this.requestId) return;
        this.groups = [];
        this.pagination.total = 0;
        this.errorMessage = message || 'Failed to fetch report';
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
};
</script>

<style>
.notice-report-dialog {
  max-width: calc(100vw - 24px);
}
/* semi-bold, sama seperti judul dialog Add/Edit Notice */
.notice-report-dialog .el-dialog__title {
  font-weight: 600;
  word-break: normal;
  overflow-wrap: anywhere;
}
/* judul, baris Target, tab, dan daftar user berjarak sama: sekitar 20px (10px bawah judul + 10px ini) */
.notice-report-dialog .el-dialog__body {
  padding-top: 10px;
}
.notice-report {
  color: #222a42;
}
/* di layar sempit Select Company turun ke bawah Target */
.notice-report__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
/* label di atas isi, gayanya sama seperti label form Add/Edit Notice */
.notice-report__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.notice-report__field-label {
  font-weight: 600;
}
/* label Target sejajar dengan label Select Company (kolom rata atas); isinya sedikit lebih dekat ke label */
.notice-report__target-value {
  line-height: 30px;
}
/* selebar kotak Search */
.notice-report__root-select {
  width: 300px;
  max-width: 100%;
}
.notice-report__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  /* jarak baris hanya terpakai saat Search turun ke bawah tab (HP): 20px, sama seperti jarak antar baris lain */
  gap: 20px 10px;
  margin-bottom: 20px;
}
.notice-report__tabs {
  display: inline-flex;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
}
.notice-report__tab {
  padding: 8px 14px;
  border: 0;
  background: #fff;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.notice-report__tab + .notice-report__tab {
  border-left: 1px solid #dcdfe6;
}
.notice-report__tab.is-active {
  background: #222a42;
  color: #fff;
}
.notice-report__search {
  width: 300px;
  max-width: 100%;
}
.notice-report__groups {
  max-height: 55vh;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}
.notice-report__group + .notice-report__group {
  border-top: 1px solid #ebeef5;
}
.notice-report__group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  background: #f5f7fa;
  color: #222a42;
  text-align: left;
  cursor: pointer;
}
.notice-report__group-name {
  flex: 1;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.notice-report__group-count {
  font-weight: 600;
  white-space: nowrap;
}
.notice-report__users {
  overflow-x: auto;
}
.notice-report__users table {
  width: 100%;
  font-size: 13px;
}
.notice-report__users td {
  padding: 8px 12px 8px 30px;
  border-top: 1px solid #ebeef5;
  vertical-align: top;
  overflow-wrap: anywhere;
}
.notice-report__users td + td {
  padding-left: 12px;
}
.notice-report__user-name {
  font-weight: 600;
}
.notice-report__user-role {
  white-space: nowrap;
}
.notice-report__user-date {
  color: #16a34a;
  white-space: nowrap;
}
/* lebar kolom tetap supaya nama, email, role, dan tanggal sejajar antar bagian; di HP tetap otomatis */
@media (min-width: 768px) {
  .notice-report__users table {
    table-layout: fixed;
  }
  .notice-report__user-name {
    width: 30%;
  }
  .notice-report__user-role,
  .notice-report__user-date {
    width: 130px;
  }
}
.notice-report__empty {
  padding: 24px 12px;
  text-align: center;
  color: #6b7280;
}
.notice-report__empty--small {
  padding: 12px;
  border-top: 1px solid #ebeef5;
}
.notice-report__section-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px 8px 30px;
  border-top: 1px solid #ebeef5;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.notice-report__section-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 12px 6px 30px;
  border-top: 1px solid #ebeef5;
}
.notice-report__section-footer .pagination {
  margin: 0;
}
/* target Agency + Client: tiap bagian jadi kotak bergaris kiri dengan jeda 8px, judulnya berlatar abu muda */
.notice-report__section--boxed {
  margin: 8px 12px 8px 30px;
  border: 1px solid #ebeef5;
  border-left: 3px solid #94a3b8;
  border-radius: 6px;
  overflow: hidden;
}
.notice-report__section--boxed + .notice-report__section--boxed {
  margin-top: 0;
}
.notice-report__section--boxed .notice-report__section-header {
  padding: 8px 12px;
  border-top: 0;
  background: #f8fafc;
  color: #4b5563;
}
/* isi bagian sejajar dengan teks judul (setelah ikon) */
.notice-report__section--boxed td:first-child,
.notice-report__section--boxed .notice-report__section-footer {
  padding-left: 30px;
}
/* HP: tiap user bertumpuk (nama, email, lalu role · tanggal) supaya nama dan email tidak terpecah per huruf */
@media (max-width: 767px) {
  .notice-report__users table,
  .notice-report__users tbody,
  .notice-report__users tr,
  .notice-report__users td {
    display: block;
  }
  .notice-report__users tr {
    padding: 8px 12px 8px 30px;
    border-top: 1px solid #ebeef5;
  }
  .notice-report__users td,
  .notice-report__users td + td,
  .notice-report__section--boxed td:first-child {
    padding: 0;
    border-top: 0;
  }
  .notice-report__users .notice-report__user-role,
  .notice-report__users .notice-report__user-date {
    display: inline;
    color: #6b7280;
  }
  .notice-report__users .notice-report__user-date {
    color: #16a34a;
  }
  .notice-report__user-date::before {
    content: '\00B7';
    margin: 0 6px;
    color: #6b7280;
  }
}
/* dialog selalu putih, sedangkan warna angka pagination mengikuti tema (putih di tema gelap) */
.notice-report-dialog .pagination .page-item:not(.active) .page-link {
  color: #222a42;
}
.notice-report-dialog .pagination .page-item:not(.active) .page-link:hover {
  background-color: #f5f7fa;
}
.notice-report__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}
.notice-report__footer .pagination {
  margin: 0;
}
.notice-report__showing {
  font-size: 13px;
  color: #6b7280;
}
</style>
